// Imports
// React and React Router
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Components
import { TunedCircuitLogo } from '../modules/common/TunedCircuitLogo';
import ConfirmAccountForm from '../modules/auth/components/forms/ConfirmAccountForm';
import ResetPasswordConfirmForm from '../modules/auth/components/forms/ResetPasswordConfirmForm';
import ResetPasswordRequestForm from '../modules/auth/components/forms/ResetPasswordRequestForm';
import SignUpForm from '../modules/auth/components/forms/SignUpForm';
import SignInForm from '../modules/auth/components/forms/SignInForm';

// Hooks
import { useAuthContext } from '../contexts/AuthContext';

// Project-level imports
import { ProfileType } from '../types/profileTypes';

// Component Definition
const AuthPage: React.FC = () => {
  // Hooks
  const navigate = useNavigate();
  const location = useLocation();
  const {
    clearError,
    confirmationEmail,
    confirmSignUp,
    error,
    forgotPassword,
    forgotPasswordSubmit,
    isLoading,
    needsConfirmation,
    signIn,
    signUp,
  } = useAuthContext();

  // State Management
  const [mode, setMode] = useState<
    'signIn' | 'signUp' | 'resetRequest' | 'resetConfirm' | 'confirmAccount'
  >('signIn');
  const [resetEmail, setResetEmail] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const modeParam = params.get('mode');
    if (modeParam === 'signUp' || modeParam === 'signIn') {
      setMode(modeParam);
    }
  }, [location.search]);

  // Event Handlers
  const handleSignIn = async (email: string, password: string) => {
    const result = await signIn(email, password);
    if (result.success) navigate('/calendar');
  };

  const handleSignUp = async (
    email: string,
    password: string,
    address: string,
    preferredUsername: string,
    profileType: ProfileType
  ) => {
    const result = await signUp(email, password, address, preferredUsername, profileType);
    if (result.success && !result.needsConfirmation) {
      await handleSignIn(email, password);
    }
  };

  const handleConfirmSignUp = async (email: string, code: string) => {
    const result = await confirmSignUp(email, code);
    if (result.success) navigate('/calendar');
  };

  const handleResetPasswordRequest = async (email: string) => {
    setResetEmail(email);
    const result = await forgotPassword(email);
    if (result.success) handleModeChange('resetConfirm');
  };

  const handleResetPasswordConfirm = async (email: string, code: string, newPassword: string) => {
    const result = await forgotPasswordSubmit(email, code, newPassword);
    if (result.success) handleModeChange('signIn');
  };

  const handleModeChange = (newMode: typeof mode) => {
    setMode(newMode);
    clearError();
  };

  const onClearError = () => {
    clearError();
  };

  // Main Render
  return (
    <div
      className={`flex h-[100dvh] flex-col ${mode === 'signUp' ? 'items-center pt-8' : 'items-center justify-center'} bg-gray-50 px-6 py-6 dark:bg-neutral-900 sm:px-6 sm:py-8 md:py-12 lg:px-8 lg:py-16`}
    >
      <TunedCircuitLogo className="mb-8" />
      <div className="mx-auto w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-4 shadow-lg dark:border-neutral-800 dark:bg-neutral-900 dark:shadow-[0_4px_32px_0_rgba(255,255,255,0.10)] sm:max-w-lg sm:p-6 md:max-w-xl md:p-8 lg:max-w-2xl lg:p-12">
        {mode === 'signIn' && (
          <SignInForm
            onSubmit={handleSignIn}
            onSwitchToSignUp={() => handleModeChange('signUp')}
            onSwitchToResetPassword={() => handleModeChange('resetRequest')}
            isLoading={isLoading}
            error={error}
          />
        )}
        {mode === 'signUp' && !needsConfirmation && (
          <SignUpForm
            onSubmit={handleSignUp}
            onSwitchToSignIn={() => handleModeChange('signIn')}
            isLoading={isLoading}
            error={error}
            onClearError={() => onClearError()}
          />
        )}
        {mode === 'resetRequest' && (
          <ResetPasswordRequestForm
            onSubmit={handleResetPasswordRequest}
            onSwitchToSignIn={() => handleModeChange('signIn')}
            isLoading={isLoading}
            error={error}
          />
        )}
        {mode === 'resetConfirm' && (
          <ResetPasswordConfirmForm
            onSubmit={handleResetPasswordConfirm}
            onSwitchToSignIn={() => handleModeChange('signIn')}
            isLoading={isLoading}
            error={error}
            email={resetEmail}
          />
        )}
        {mode === 'signUp' && needsConfirmation && (
          <ConfirmAccountForm
            email={confirmationEmail}
            onSubmit={handleConfirmSignUp}
            isLoading={isLoading}
            error={error}
            onSwitchToSignIn={() => handleModeChange('signIn')}
          />
        )}
      </div>
    </div>
  );
};

// Export
export default AuthPage;
